import { useRef, useCallback } from 'react';

/**
 * useSwipeNavigation - Detects horizontal swipe gestures for tab navigation.
 *
 * @param {function} onSwipeLeft - Called when user swipes left (go to next tab)
 * @param {function} onSwipeRight - Called when user swipes right (go to previous tab)
 * @param {object} options - { threshold: 50, allowedTime: 300 }
 * @returns {{ onTouchStart, onTouchEnd }} - Touch event handlers to spread on the container
 */
export default function useSwipeNavigation(onSwipeLeft, onSwipeRight, options = {}) {
    const { threshold = 50, allowedTime = 500 } = options;
    const touchStart = useRef({ x: 0, y: 0, time: 0 });

    const onTouchStart = useCallback((e) => {
        const touch = e.touches[0];
        touchStart.current = {
            x: touch.clientX,
            y: touch.clientY,
            time: Date.now(),
        };
    }, []);

    const onTouchEnd = useCallback((e) => {
        const touch = e.changedTouches[0];
        const dx = touch.clientX - touchStart.current.x;
        const dy = touch.clientY - touchStart.current.y;
        const dt = Date.now() - touchStart.current.time;

        // Only count horizontal swipes (not vertical scrolling)
        if (dt <= allowedTime && Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy) * 1.5) {
            if (dx < 0 && onSwipeLeft) {
                onSwipeLeft();
            } else if (dx > 0 && onSwipeRight) {
                onSwipeRight();
            }
        }
    }, [onSwipeLeft, onSwipeRight, threshold, allowedTime]);

    return { onTouchStart, onTouchEnd };
}
